import React from 'react';
import { ExternalLink, MapPin } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { Section, Eyebrow, FadeIn, PageHero } from '../components/ui-bits';
import Breadcrumb from '../components/Breadcrumb';

const DelabaseBadge = () => (
  <a
    href="https://delabase.cl"
    target="_blank"
    rel="noopener noreferrer"
    data-testid="delabase-badge"
    className="inline-flex items-center gap-4 rounded-2xl border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] hover:border-amber-300/40 pl-3 pr-6 py-3 transition-colors"
  >
    <img
      src="https://customer-assets.emergentagent.com/job_bringing-light/artifacts/i9rrxzk0_Logo%20Constructora%20Salinas.png"
      alt="Delabase Inmobiliarias / Constructora Salinas"
      className="h-14 w-auto object-contain"
      loading="lazy"
    />
    <span className="hidden sm:inline-block text-[11px] uppercase tracking-[0.2em] text-white/55 pl-3 border-l border-white/15 leading-tight">
      Inmobiliaria<br />Delabase
    </span>
  </a>
);

const InversionInmobiliaria = () => {
  const { t, lang } = useLanguage();

  const reasons = [
    { title: t.v2Page.why1Title, body: t.v2Page.why1Body },
    { title: t.v2Page.why2Title, body: t.v2Page.why2Body },
    { title: t.v2Page.why3Title, body: t.v2Page.why3Body },
  ];

  const projects = [
    {
      name: t.v2Page.p1,
      loc: t.v2Page.p1Loc,
      img: 'https://delabase.cl/wp-content/uploads/2026/01/portada.jpg',
      url: 'https://delabase.cl/proyectos/edificio-volcan/',
    },
    {
      name: t.v2Page.p2,
      loc: t.v2Page.p2Loc,
      img: 'https://delabase.cl/wp-content/uploads/2025/03/imagen-proyect-biaut2.jpg',
      url: 'https://delabase.cl/proyectos/augusto-biaut/',
    },
    {
      name: t.v2Page.p3,
      loc: t.v2Page.p3Loc,
      img: 'https://delabase.cl/wp-content/uploads/2024/10/img-Carvajal-1.jpg',
      url: 'https://delabase.cl/proyectos/carvajal-0330/',
    },
    {
      name: t.v2Page.p4,
      loc: t.v2Page.p4Loc,
      img: 'https://delabase.cl/wp-content/uploads/2024/10/Don-Claudio_0012_DJI_0225-1.jpg',
      url: 'https://delabase.cl/proyectos/don-claudio/',
    },
  ];

  return (
    <div data-testid="vertical-2-page">
      <Breadcrumb items={[
        { to: '/que-hacemos', label: lang === 'en' ? 'What We Do' : 'Qué Hacemos' },
        { to: null, label: t.verticals.v2.title },
      ]} />
      <PageHero eyebrow={t.v2Page.eyebrow} title={t.v2Page.title} intro={t.v2Page.intro} />

      <Section className="pb-20">
        <FadeIn>
          <div className="flex flex-wrap items-center gap-5">
            <span className="text-xs uppercase tracking-[0.2em] text-white/45">
              En alianza con
            </span>
            <DelabaseBadge />
          </div>
        </FadeIn>
      </Section>

      <Section className="pb-24" dataTestId="v2-reasons">
        <FadeIn>
          <Eyebrow>{t.v2Page.whyTitle}</Eyebrow>
        </FadeIn>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {reasons.map((r, i) => (
            <FadeIn key={i} delay={i * 0.08}>
              <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-8 h-full card-hover">
                <div className="font-display text-amber-300 text-sm tracking-widest">0{i + 1}</div>
                <h4 className="mt-4 font-display font-bold text-xl text-white tracking-tight">{r.title}</h4>
                <p className="mt-3 font-body text-white/55 leading-relaxed">{r.body}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      <Section className="pb-24" dataTestId="v2-projects">
        <FadeIn>
          <Eyebrow>{t.v2Page.projectsTitle}</Eyebrow>
        </FadeIn>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {projects.map((p, i) => (
            <FadeIn key={i} delay={i * 0.06}>
              <a
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`v2-project-${i + 1}`}
                className="group rounded-2xl overflow-hidden border border-white/10 bg-white/[0.025] h-full card-hover block"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={p.img}
                    alt={p.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#070d18] via-transparent to-transparent" />
                </div>
                <div className="p-6">
                  <div className="font-display text-amber-300 text-xs tracking-widest">PROYECTO 0{i + 1}</div>
                  <h4 className="mt-3 font-display font-bold text-xl text-white tracking-tight leading-snug">
                    {p.name}
                  </h4>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-sm text-white/55">
                    <MapPin className="w-3.5 h-3.5" />
                    {p.loc}
                  </div>
                </div>
              </a>
            </FadeIn>
          ))}
        </div>
      </Section>

      <Section className="pb-32">
        <FadeIn>
          <a
            href="https://delabase.cl"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-7 h-12 rounded-full bg-amber-300 text-[#0a1322] font-semibold text-sm hover:bg-amber-200 transition-colors"
            data-testid="v2-delabase-link"
          >
            delabase.cl <ExternalLink className="w-4 h-4" />
          </a>
        </FadeIn>
      </Section>
    </div>
  );
};

export default InversionInmobiliaria;
